import { isEmpty } from "lodash";
import { useEffect, useMemo, useRef } from "react";

import { Box } from "@/Components/v2/atoms/Box";
import { Divider } from "@/Components/v2/atoms/Divider";
import { CircularProgress } from "@/Components/v2/atoms/Progress";
import RoundedBorderContainer from "@/Components/v2/atoms/RoundedBorderContainer/RoundedBorderContainer";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Typography } from "@/Components/v2/atoms/Typography";
import { StateInfoGuide } from "@/Components/v2/molecule/StateInfoGuide";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useConfig } from "@/hooks/useConfig";
import { Toggle, ToolBarShared } from "@/shared/components/DataGrid/ToolBarShared";
import DataGridContainer from "@/shared/components/DataGridContainer";
import { useDateRangeStore } from "@/shared/components/date-range/store/useDateRangeStore";
import Title from "@/shared/components/Sections/Title";
import { useMetricOptions } from "@/shared/hooks/useMetricOptions";
import { MetricType } from "@/shared/types";
import { ScenarioNode } from "@/shared/utils/nodes";
import { Palette } from "@/Utils/Colors/palette";
import { Layer } from "@/Utils/hierarchy";

import { useChannels } from "../channel-roi/api/getChannels";
import { useSaturationCurves } from "./api/getSaturationCurves";
import GroupCurveContainer from "./components/Charts/GroupCurveContainer";
import SingleCurveContainer from "./components/Charts/SingleCurveContainer";
import Header from "./components/Sections/Header";
import HeaderInfoTooltip from "./components/Sections/HeaderInfoTooltip";
import SaturationCurvesViewSelector from "./components/Sections/SaturationCurveViewSelector";
import { ScenarioBanner } from "./components/Sections/ScenarioBanner";
import SummaryData from "./components/Sections/SummaryData";
import { DEFAULT_COLUMNS } from "./constants/defaults";
import { InvestmentRating } from "./constants/maps";
import { EMPTY_STATE_INFO, INSUFFICIENT_DATA_INFO } from "./constants/stateInfo";
import { getColumnDefinitions } from "./getColumnDefinitions";
import { calculateWeeklyMetricAverages } from "./helpers/calculateWeeklyMetricAverages";
import { transformSaturationCurvesToGraphData } from "./helpers/formatSaturationCurveData";
import { SaturationCurvesView, useInvestmentLevelsStore } from "./store/useInvestmentLevelsStore";
import { ComparisonMode } from "./types/comparisonMode";
import { GraphDataDTO } from "./types/dto";

export const content = {
  title: "Investment Levels",
  subtitle:
    "See how your ROI changes as you increase spending on a specific media channel. Use this to plan spend across channels or grow revenue with more budget.",
};

const InvestmentLevels = () => {
  const palette = new Palette();
  const { config } = useConfig();
  const { nodes, loading } = useInfo();
  const { minDate, maxDate, startDate, endDate } = useDateRangeStore();
  const { targetNodeUUID, channels, comparisonMode, geography } = useInvestmentLevelsStore(
    (state) => state.filters
  );
  const { metricType } = useInvestmentLevelsStore((state) => state.grid);
  const saturationCurvesView = useInvestmentLevelsStore((state) => state.saturationCurvesView);
  const activeColumns = useInvestmentLevelsStore((state) => state.grid.activeColumns);
  const metricOptions = useMetricOptions();

  const {
    setSaturationCurvesView,
    setTargetNodeUUID,
    setComparisonMode,
    setChannels,
    setGeography,
    setActiveColumns,
    setMetricType,
  } = useInvestmentLevelsStore();

  const formatCurrency = config.currency.formatCurrency;
  const apiRef = useRef(null);

  useEffect(() => {
    if (nodes && !loading && targetNodeUUID === null) {
      const defaultNodeUUID = nodes?.getHierarchy({ shouldHidePortfolio: true })[0]?.uuid;
      if (defaultNodeUUID !== undefined) {
        setTargetNodeUUID(defaultNodeUUID);
      }
    }
  }, [loading, targetNodeUUID, nodes]);

  const handleComparisonModeChange = (mode: ComparisonMode) => {
    // Set channels to a single selected channel if we are not in channel comparison mode
    if (mode != ComparisonMode.CHANNEL) {
      if (channels.length > 0 && channels[0]) {
        setChannels([channels[0]]);
      }
    }

    setComparisonMode(mode);
  };

  const handleChannelSelectionChange = (channels: string[]) => {
    setChannels(channels);
  };

  const handleGeoSelectionChange = (geography: string) => {
    setGeography(geography);
  };

  const targetNode: ScenarioNode | undefined = targetNodeUUID
    ? (nodes?.getNode(targetNodeUUID)?.node as ScenarioNode | undefined)
    : undefined;

  const { data: saturationCurves, isLoading: areSaturationCurvesLoading } = useSaturationCurves({
    brand: targetNode?.brand || "",
    category: targetNode?.category || "",
    product: targetNode?.product || "",
    startDate,
    endDate,
    palette,
    geography: comparisonMode === ComparisonMode.GEOGRAPHY ? undefined : geography,
    comparisonMode,
    channels,
    config: {
      enabled: !!targetNodeUUID && !!minDate && !!maxDate && !!targetNode,
    },
  });

  const { data: allChannels = [] } = useChannels({
    type: "saturation-curves",
    startDate,
    endDate,
    brand: targetNode?.brand || "",
    category: targetNode?.category || "",
    product: targetNode?.product || "",
    geography,
    comparisonMode,
    config: {
      enabled: !!startDate && !!endDate && !!targetNode,
    },
  });

  const { initInfo: info } = useInfo();

  const filteredGeoOptions = useMemo(() => {
    if (!info?.geographies) return [];
    return ["national", ...info.geographies];
  }, [info?.geographies]);

  const [geoData] = info?.geographies || [];

  const hasGeography = Boolean(geoData && geoData.length > 0);

  useEffect(() => {
    if (comparisonMode === ComparisonMode.CHANNEL) {
      if (allChannels.length > 0) {
        setChannels(allChannels);
      }
      return;
    }
  }, [comparisonMode, allChannels]);

  // Add a ref to track previous comparison mode
  const prevComparisonModeRef = useRef(comparisonMode);

  useEffect(() => {
    if (prevComparisonModeRef.current !== comparisonMode) {
      if (
        comparisonMode === ComparisonMode.GEOGRAPHY ||
        comparisonMode === ComparisonMode.BRAND ||
        comparisonMode === ComparisonMode.CATEGORY ||
        comparisonMode === ComparisonMode.PRODUCT
      ) {
        if (!saturationCurves?.length) return;

        const firstChannelWithSufficientData = saturationCurves?.find(
          (curve) => curve.curveValue > 0
        );

        const defaultChannel = saturationCurves?.[0]?.channel || "";
        const selectedChannel = [firstChannelWithSufficientData?.channel || defaultChannel];
        const selectedGeography = firstChannelWithSufficientData?.geography || "";
        setChannels(selectedChannel);
        if (hasGeography) {
          setGeography(selectedGeography);
        }
      }
      // Update the ref
      prevComparisonModeRef.current = comparisonMode;
    }
  }, [comparisonMode, saturationCurves, targetNodeUUID, hasGeography, setChannels, setGeography]);

  const mappedGraphData = useMemo(() => {
    if (!nodes) return [];

    return transformSaturationCurvesToGraphData(saturationCurves, nodes);
  }, [saturationCurves, nodes]);

  const weeklyMetricAverages = calculateWeeklyMetricAverages(saturationCurves);

  const hasInsufficientData =
    mappedGraphData.length > 0 &&
    mappedGraphData.every((curve) => curve.rating === InvestmentRating.INSUFFICIENT_DATA);

  const hasNoData = !saturationCurves?.length;

  const singleCurve = mappedGraphData.length === 1 ? mappedGraphData[0] : undefined;

  const handleRowClick = (row: GraphDataDTO) => {
    const { channel, nodeUUID } = row;
    setSaturationCurvesView(SaturationCurvesView.CURVES);
    setComparisonMode(ComparisonMode.CHANNEL);
    if (nodeUUID) setTargetNodeUUID(nodeUUID as string);
    setChannels([channel]);
  };

  const columns = getColumnDefinitions(activeColumns, {
    formatCurrency,
    handleRowClick,
    metricType,
    comparisonMode,
    config,
  });

  const { moduleOpened } = useAnalytics();

  useEffect(() => {
    moduleOpened("investment-levels-v2", Layer.Portfolio);
  }, []);

  if (loading || !nodes || targetNodeUUID === null) {
    return <CircularProgress size={150} sx={{ marginY: "30rem" }} />;
  }

  const renderStateInfo = () => {
    if (hasInsufficientData) {
      return <StateInfoGuide {...INSUFFICIENT_DATA_INFO} />;
    }
    if (hasNoData) {
      return <StateInfoGuide {...EMPTY_STATE_INFO} />;
    }
    return null;
  };

  const toggles: Toggle[] = [];

  const metricDropdownConfig = {
    label: "Show",
    value: metricType,
    options: metricOptions,
    onChange: (value: string | null) => {
      if (value) {
        setMetricType(value as MetricType);
      }
    },
  };

  return (
    <Box>
      <Box marginBottom={3}>
        <Title title={content.title} subtitle={content.subtitle} />
        <Header
          hasGeography={hasGeography}
          config={config}
          nodes={nodes}
          selectedNodeUUID={targetNodeUUID}
          channelOptions={allChannels}
          geographyOptions={filteredGeoOptions}
          onTargetNodeChange={setTargetNodeUUID}
          onComparisonModeChange={handleComparisonModeChange}
          onChannelSelectionChange={handleChannelSelectionChange}
          onGeoSelectionChange={handleGeoSelectionChange}
        />
        {!isEmpty(weeklyMetricAverages) && !hasInsufficientData && !hasNoData && (
          <Stack marginX={4} gap={1}>
            <SummaryData {...weeklyMetricAverages} />
            <Stack alignItems="flex-end">
              <HeaderInfoTooltip />
            </Stack>
          </Stack>
        )}
      </Box>
      {areSaturationCurvesLoading || !targetNodeUUID ? (
        <Stack justifyContent="center" alignItems="center" sx={{ minHeight: "50rem" }}>
          <CircularProgress size={150} />
        </Stack>
      ) : (
        <Box sx={{ marginX: 4, marginBottom: 4 }}>
          {renderStateInfo() ? (
            renderStateInfo()
          ) : (
            <RoundedBorderContainer>
              <Stack
                sx={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 2 }}
              >
                <Typography variant="h2" fontSize={24} fontWeight={600}>
                  Saturation Curves
                </Typography>
                <SaturationCurvesViewSelector />
              </Stack>
              <Divider sx={{ marginBottom: 2 }} />
              {saturationCurvesView === SaturationCurvesView.CURVES ? (
                singleCurve ? (
                  <SingleCurveContainer curve={singleCurve} config={config} />
                ) : (
                  <GroupCurveContainer curves={mappedGraphData} config={config} />
                )
              ) : (
                <DataGridContainer
                  apiRef={apiRef}
                  rows={mappedGraphData}
                  columns={columns}
                  metricDropdown={metricDropdownConfig}
                  toolbarComponent={
                    <ToolBarShared
                      apiRef={apiRef}
                      columns={columns.filter((column) => column.field !== "handle_click")}
                      activeColumns={activeColumns}
                      toggles={toggles}
                      handleApplyFilters={({ activeColumns }) => {
                        setActiveColumns(activeColumns);
                      }}
                      handleResetFilters={() => {
                        setActiveColumns(DEFAULT_COLUMNS);
                        setMetricType(MetricType.MARKETING_ROI);
                      }}
                    />
                  }
                  options={{
                    rowHeight: 70,
                    sortModel: [{ field: "spend", sort: "desc" }],
                  }}
                />
              )}
            </RoundedBorderContainer>
          )}
          <ScenarioBanner />
        </Box>
      )}
    </Box>
  );
};

export default InvestmentLevels;
